import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Send, Ship } from "lucide-react";
import { api } from "@/lib/api";
import { useTelecomAccess } from "@/hooks/useTelecomAccess";
import { SectorNoHabilitadoPanel } from "@/components/SectorNoHabilitadoPanel";
import { PaisesPicker } from "@/components/PaisesPicker";
import { TitularPicker } from "@/components/TitularPicker";
import { ResponsablePicker } from "@/components/ResponsablePicker";
import {
  CapturaDocumentos,
  subirDocumentosCapturados,
  type DocumentoCapturado,
} from "@/components/CapturaDocumentos";
import {
  Alert,
  Button,
  Card,
  DataField,
  FormField,
  Input,
  PageContainer,
  PageHeader,
  Select,
  Textarea,
} from "@/components/ui";

/**
 * Alta de una solicitud de Carta Aduana (#15) — formato NPM-T0037-R12.
 *
 * Trámite PRE-certificación: no parte de un certificado vigente. Ampara la
 * internación de muestras de producto no certificado con destino a pruebas de
 * laboratorio; por eso captura normas del formato, número de muestras, aduana
 * (nombre y número, RN-08) y laboratorio destino (RN-07).
 *
 * Diseño funcional: Insumos/Servicios-CorrecciónDeCartaAduana/
 * diseno-funcional-carta-aduana-telecom-sicen-npmx.md
 */

interface Norma { id: string; codigo: string; titulo: string; tipo: string; }
interface Aduana { id: string; numero: string; nombre: string; }
interface Laboratorio { id: string; razonSocial: string; nombreCorto: string; }
interface Pais { id: string; codigo: string; nombre: string; }
interface Titular { id: string; razonSocial: string; rfc: string; estatus: string; }
interface ContextoMio {
  titularId: string;
  titularRazonSocial: string;
  tramitadorId: string | null;
  tramitadorRazonSocial: string | null;
  titularPuedeActuarComoTitular: boolean;
  titularPuedeActuarComoTramitador: boolean;
}

const CONTEXTO_TIPO = "SOLICITUD_TELECOM";

const DECLARACION =
  "Declaro bajo protesta de decir verdad que el producto se someterá a pruebas de " +
  "laboratorio necesarias para obtener la certificación con la NOM que le corresponda " +
  "a través de NPMX.";

export function CartaAduanaAltaPage() {
  const access = useTelecomAccess();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const titularIdInicial =
    params.get("titularId") ?? window.sessionStorage.getItem("sicen.contexto.titularId");

  // ----- Estado del formulario ------------------------------------------------
  const [titular, setTitular] = useState<Titular | null>(null);
  const [normasSel, setNormasSel] = useState<string[]>([]);
  const [productoNombre, setProductoNombre] = useState("");
  const [marca, setMarca] = useState("");
  const [modelo, setModelo] = useState("");
  const [seriesText, setSeriesText] = useState("");
  const [sinSerie, setSinSerie] = useState(false);
  const [justifSerie, setJustifSerie] = useState("");
  const [accesorios, setAccesorios] = useState("S/A");
  const [paisesOrigen, setPaisesOrigen] = useState<string[]>([]);
  const [paisesProcedencia, setPaisesProcedencia] = useState<string[]>([]);
  const [fraccionesText, setFraccionesText] = useState("98060001");
  const [muestras, setMuestras] = useState(1);
  const [aduanasSel, setAduanasSel] = useState<string[]>([]);
  const [laboratorio, setLaboratorio] = useState("");
  const [representante, setRepresentante] = useState("");
  const [declaracion, setDeclaracion] = useState(false);
  const [observaciones, setObservaciones] = useState("");
  const [docs, setDocs] = useState<DocumentoCapturado[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [solicitudCreada, setSolicitudCreada] = useState<string | null>(null);

  // ----- Contexto (titular/tramitador desde Registro Legal) --------------------
  const qContextos = useQuery<ContextoMio[]>({
    queryKey: ["contextos-mios"],
    queryFn: async () => (await api.get<ContextoMio[]>("/api/contextos/mios")).data,
    enabled: access.allowed,
  });
  const ctxActivo = qContextos.data?.find((c) => c.titularId === titularIdInicial) ?? null;
  const ctxEsTramitadorPuro =
    !!ctxActivo && !ctxActivo.titularPuedeActuarComoTitular && ctxActivo.titularPuedeActuarComoTramitador;
  const esExterno = !access.esInterno;
  const titularBloqueado = esExterno && !!ctxActivo?.titularPuedeActuarComoTitular;

  useEffect(() => {
    if (!ctxActivo || titular) return;
    if (ctxActivo.titularPuedeActuarComoTitular) {
      setTitular({ id: ctxActivo.titularId, razonSocial: ctxActivo.titularRazonSocial, rfc: "", estatus: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctxActivo]);

  const tramitadorId = ctxActivo?.tramitadorId ?? (ctxEsTramitadorPuro ? ctxActivo?.titularId : null);
  const tramitadorNombre =
    ctxActivo?.tramitadorRazonSocial ?? (ctxEsTramitadorPuro ? ctxActivo?.titularRazonSocial : null);

  // ----- Catálogos --------------------------------------------------------------
  const qCatalogos = useQuery<{ normas: Norma[]; aduanas: Aduana[] }>({
    queryKey: ["telecom-carta-aduana-catalogos"],
    queryFn: async () =>
      (await api.get<{ normas: Norma[]; aduanas: Aduana[] }>(
        "/api/sector/telecom/carta-aduana/catalogos",
      )).data,
    enabled: access.allowed,
  });
  const qPaises = useQuery<Pais[]>({
    queryKey: ["telecom-paises"],
    queryFn: async () => (await api.get<Pais[]>("/api/sector/telecom/paises")).data,
    enabled: access.allowed,
  });
  const qLabs = useQuery<{ laboratorios: Laboratorio[]; alcanceCompleto: boolean }>({
    queryKey: ["telecom-carta-aduana-labs", [...normasSel].sort().join(",")],
    queryFn: async () =>
      (await api.get<{ laboratorios: Laboratorio[]; alcanceCompleto: boolean }>(
        `/api/sector/telecom/carta-aduana/laboratorios?normas=${normasSel.join(",")}`,
      )).data,
    enabled: access.allowed && normasSel.length > 0,
  });

  // Si el laboratorio elegido deja de estar en la lista (cambio de normas), límpialo.
  useEffect(() => {
    if (laboratorio && qLabs.data && !qLabs.data.laboratorios.some((l) => l.id === laboratorio)) {
      setLaboratorio("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qLabs.data]);

  // ----- Validación (RN-01…RN-13 en su parte de captura) -------------------------
  const series = useMemo(
    () => seriesText.split(",").map((s) => s.trim()).filter(Boolean),
    [seriesText],
  );
  const fracciones = useMemo(
    () => fraccionesText.split(",").map((f) => f.trim()).filter(Boolean),
    [fraccionesText],
  );
  const fraccionesInvalidas = fracciones.filter((f) => !/^\d{8}$/.test(f));

  const valido =
    !!titular &&
    normasSel.length > 0 &&
    productoNombre.trim() !== "" &&
    marca.trim() !== "" &&
    modelo.trim() !== "" &&
    (sinSerie ? justifSerie.trim() !== "" : series.length > 0) &&
    paisesOrigen.length > 0 &&
    paisesProcedencia.length > 0 &&
    fracciones.length > 0 &&
    fraccionesInvalidas.length === 0 &&
    muestras >= 1 &&
    aduanasSel.length > 0 &&
    laboratorio !== "" &&
    representante.trim() !== "" &&
    declaracion;

  const enviar = async () => {
    if (!valido || !titular) return;
    setError(null);
    setEnviando(true);
    try {
      let id = solicitudCreada;
      if (!id) {
        id = (
          await api.post<{ id: string }>("/api/sector/telecom/solicitudes/carta-aduana", {
            titularId: titular.id,
            tramitadorId: tramitadorId ?? null,
            normasIds: normasSel,
            productoNombre: productoNombre.trim(),
            marca: marca.trim(),
            productoModelo: modelo.trim(),
            series: sinSerie ? [] : series,
            sinSerie,
            justificacionSerie: sinSerie ? justifSerie.trim() : null,
            accesorios: accesorios.trim() || "S/A",
            paisesOrigenIds: paisesOrigen,
            paisesProcedenciaIds: paisesProcedencia,
            fracciones,
            numMuestras: muestras,
            aduanasIds: aduanasSel,
            laboratorioId: laboratorio,
            representanteNombre: representante.trim(),
            declaracionAceptada: declaracion,
            observaciones: observaciones.trim() || null,
          })
        ).data.id;
        setSolicitudCreada(id);
      }
      const fallidos = docs.length
        ? await subirDocumentosCapturados(docs, setDocs, CONTEXTO_TIPO, id)
        : 0;
      if (fallidos > 0) {
        setError(
          `La solicitud se registró, pero ${fallidos} documento(s) no se pudieron subir. ` +
            `Reintenta los marcados en rojo o continúa y adjúntalos desde el detalle de la solicitud.`,
        );
        setEnviando(false);
        return;
      }
      navigate(`/sector/telecom/solicitudes/${id}/success`);
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ?? e?.response?.data?.message ?? "No se pudo registrar la solicitud.",
      );
      setEnviando(false);
    }
  };

  const toggle = (lista: string[], setLista: (v: string[]) => void, id: string) =>
    setLista(lista.includes(id) ? lista.filter((x) => x !== id) : [...lista, id]);

  // ----- Guards -----------------------------------------------------------------
  if (access.loading) {
    return (
      <div className="flex items-center justify-center p-12 text-sm text-fg-subtle">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validando permisos…
      </div>
    );
  }
  if (!access.allowed) {
    return (
      <SectorNoHabilitadoPanel
        sectorNombre="Telecomunicaciones"
        sectorCodigo="TELECOM"
        titularRazonSocial={access.titularRazonSocial}
        sinContexto={access.sinContexto}
      />
    );
  }

  return (
    <PageContainer width="form">
      <PageHeader
        backTo="/telecom/servicios"
        backLabel="Menú de servicios"
        icon={<Ship className="h-6 w-6" />}
        title="Solicitud de Carta Aduana"
        subtitle="Formato NPM-T0037-R12 · Servicio #15 · Cartas y Anexos"
      />

      <Alert variant="info" title="¿Qué es la Carta Aduana?">
        Documento que ampara ante la aduana la internación de <strong>muestras de producto
        aún no certificado</strong>, con el compromiso de someterlas a pruebas de laboratorio
        para obtener la certificación. No requiere certificado previo en SICEN.
      </Alert>

      {/* Solicitante */}
      <Card title="Solicitante">
        {titularBloqueado || (titular && esExterno) ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <DataField label="Razón social (titular)" value={titular?.razonSocial ?? "—"} />
            {tramitadorNombre && <DataField label="Tramitador" value={tramitadorNombre} />}
          </div>
        ) : (
          <FormField
            label="Empresa titular"
            required
            hint="Busca la empresa cliente titular del producto."
          >
            <TitularPicker value={titular} onChange={setTitular} />
          </FormField>
        )}
        <p className="mt-3 text-xs text-fg-subtle">
          Datos precargados desde Registro Legal. Si cambiaron personas o circunstancias,
          actualiza el Registro Legal antes de enviar la solicitud.
        </p>
      </Card>

      {/* Norma */}
      <Card title="Norma / Disposición Técnica">
        <p className="mb-3 text-sm text-fg-subtle">
          Selecciona la(s) NOM/DT del formato aplicables al producto. Mínimo una.
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          {(qCatalogos.data?.normas ?? []).map((n) => (
            <label
              key={n.id}
              className="flex cursor-pointer items-start gap-2 rounded-md border border-border p-2 text-sm hover:bg-surface-2"
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={normasSel.includes(n.id)}
                onChange={() => toggle(normasSel, setNormasSel, n.id)}
              />
              <span>
                <span className="font-medium">{n.codigo}</span>
                <span className="block text-xs text-fg-subtle">{n.titulo}</span>
              </span>
            </label>
          ))}
        </div>
      </Card>

      {/* Producto */}
      <Card title="Datos del producto nuevo">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Nombre del producto" required className="sm:col-span-2">
            <Input value={productoNombre} onChange={(e) => setProductoNombre(e.target.value)}
                   placeholder="p. ej. Barra de sonido (Soundbar)" />
          </FormField>
          <FormField label="Marca" required>
            <Input value={marca} onChange={(e) => setMarca(e.target.value)} />
          </FormField>
          <FormField label="Modelo" required>
            <Input value={modelo} onChange={(e) => setModelo(e.target.value)} />
          </FormField>
          <FormField
            label="Número(s) de serie"
            required={!sinSerie}
            hint="Separados por coma."
            className="sm:col-span-2"
          >
            <Input
              value={seriesText}
              onChange={(e) => setSeriesText(e.target.value)}
              disabled={sinSerie}
              placeholder="p. ej. 7SG714AL500001, 7SG714AL500002"
            />
          </FormField>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={sinSerie}
              onChange={(e) => setSinSerie(e.target.checked)}
            />
            El producto no cuenta con número de serie
          </label>
          {sinSerie && (
            <FormField
              label="Justificación de la ausencia de número de serie"
              required
              className="sm:col-span-2"
            >
              <Textarea
                value={justifSerie}
                onChange={(e) => setJustifSerie(e.target.value)}
                placeholder="Explica por qué el producto no cuenta con número de serie."
              />
            </FormField>
          )}
          <FormField label="Accesorios" hint="«S/A» cuando no aplica.">
            <Input value={accesorios} onChange={(e) => setAccesorios(e.target.value)} />
          </FormField>
          <div className="sm:col-span-2 grid gap-4 sm:grid-cols-2">
            <PaisesPicker
              label="País(es) de origen"
              required
              opciones={qPaises.data ?? []}
              seleccion={paisesOrigen}
              onChange={setPaisesOrigen}
            />
            <PaisesPicker
              label="País(es) de procedencia"
              required
              opciones={qPaises.data ?? []}
              seleccion={paisesProcedencia}
              onChange={setPaisesProcedencia}
            />
          </div>
        </div>
      </Card>

      {/* Importación, muestras, aduana y laboratorio */}
      <Card title="Importación y laboratorio">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label="Fracción(es) arancelaria(s)"
            required
            hint="8 dígitos, separadas por coma. 98060001 = muestras para certificación."
            error={fraccionesInvalidas.length > 0 ? `Inválidas: ${fraccionesInvalidas.join(", ")}` : undefined}
          >
            <Input value={fraccionesText} onChange={(e) => setFraccionesText(e.target.value)} />
          </FormField>
          <FormField label="Número de muestras" required hint="Entero mayor que cero.">
            <Input
              type="number"
              min={1}
              value={muestras}
              onChange={(e) => setMuestras(parseInt(e.target.value, 10) || 0)}
            />
          </FormField>
        </div>
        <FormField
          label="Aduana(s) por la(s) que será importado"
          required
          hint="La aduana se registra con nombre y número."
          className="mt-4"
        >
          <div className="grid gap-2 sm:grid-cols-2">
            {(qCatalogos.data?.aduanas ?? []).map((a) => (
              <label
                key={a.id}
                className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm hover:bg-surface-2"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={aduanasSel.includes(a.id)}
                  onChange={() => toggle(aduanasSel, setAduanasSel, a.id)}
                />
                <span>
                  {a.nombre} <span className="text-xs text-fg-subtle">(No. {a.numero})</span>
                </span>
              </label>
            ))}
          </div>
        </FormField>
        <FormField
          label="Laboratorio al que se envían las muestras"
          required
          className="mt-4"
          hint={normasSel.length === 0 ? "Selecciona primero la(s) norma(s)." : undefined}
        >
          <Select
            value={laboratorio}
            onChange={(e) => setLaboratorio(e.target.value)}
            disabled={normasSel.length === 0 || qLabs.isLoading}
          >
            <option value="">— Selecciona —</option>
            {(qLabs.data?.laboratorios ?? []).map((l) => (
              <option key={l.id} value={l.id}>{l.razonSocial}</option>
            ))}
          </Select>
        </FormField>
        {qLabs.data && !qLabs.data.alcanceCompleto && (
          <Alert variant="warning">
            Ningún laboratorio tiene alcance registrado para todas las normas seleccionadas;
            se muestran todos los laboratorios activos. NPMX verificará el alcance durante la
            revisión.
          </Alert>
        )}
      </Card>

      {/* Anexos y observaciones */}
      <Card title="Anexos y observaciones">
        <CapturaDocumentos
          tag="TELECOM_CARTA_ADUANA"
          titulo="Documentos adicionales"
          ayuda="Opcionales. Toda la documentación debe estar en idioma español."
          docs={docs}
          onChange={setDocs}
          disabled={enviando}
        />
        <FormField label="Observaciones del usuario" className="mt-4">
          <Textarea
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            placeholder="Comentarios para NPMX (opcional)."
          />
        </FormField>
      </Card>

      {/* Declaración y firma */}
      <Card title="Representante autorizado y declaración">
        {/* RF-005: se propone desde el registro legal vigente del titular; solo
            si no hay responsables registrados se habilita la captura libre. */}
        <ResponsablePicker
          label="Nombre del representante autorizado"
          registroLegalId={titular?.id}
          value={representante}
          onChange={setRepresentante}
          required
        />
        <div className="mt-4 rounded-md border border-border bg-surface-2 p-3 text-sm italic">
          {DECLARACION}
        </div>
        <label className="mt-3 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4"
            checked={declaracion}
            onChange={(e) => setDeclaracion(e.target.checked)}
          />
          <span>
            Acepto la declaración bajo protesta de decir verdad a nombre del representante
            autorizado. La solicitud será atendida sólo si los datos se llenan por completo.
          </span>
        </label>
      </Card>

      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex justify-end gap-2">
        <Link to="/telecom/servicios">
          <Button variant="secondary" disabled={enviando}>Regresar</Button>
        </Link>
        <Button
          variant="success"
          icon={<Send className="h-4 w-4" />}
          loading={enviando}
          disabled={!valido || enviando}
          onClick={enviar}
        >
          Enviar solicitud
        </Button>
      </div>
    </PageContainer>
  );
}
